import {useCallback, useState} from 'react'
import type {PreparedPrompt} from '@github-ui/ahp-session-client/contracts'
import type {McpPermissionMode} from '@github-ui/agent-sessions-types/mcp-permission-mode'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useNavigate} from '@github-ui/use-navigate'
import {useCreateEnvironmentTask, type OptimisticTaskListConfig} from './use-create-environment-task'
import {generateTaskPathForRepo, shouldUseGlobalTaskUrls, type TaskUrlScope} from '../utils/generate-task-path'
import type {EnvironmentSubmit} from '@github-ui/one-copilot-composer/use-environment-selection'

export interface FailedEnvironmentAttempt {
  problemStatement: string
  errorMessage: string
  timestamp: string
}

// Extracted so the try/catch below contains no inline conditional value-block,
// which the React Compiler cannot compile in that position.
function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

interface UseEnvironmentTaskSubmitOptions {
  optimisticTaskListConfig?: OptimisticTaskListConfig
  /** Focused after a failure banner is dismissed so keyboard users don't lose
   * their place when the Dismiss button is removed (WCAG 2.4.3). */
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  /** Host-correct HTTPS clone URL forwarded to worktree provisioning; only sent
   * alongside a `repositoryNwo`, and falls back to the origin-derived URL when omitted. */
  repositoryCloneUrl?: string
  /** Controls post-create navigation. Existing callers remain repository-scoped
   * unless they explicitly opt into the global task-detail route. */
  taskUrlScope?: TaskUrlScope
}

/**
 * Owns AHP environment task submission for the new task input: it creates the
 * environment-bound task and navigates to it on success, and records a
 * dismissible failure banner while re-throwing on error so `ChatInput` can exit
 * its "Creating..." state and re-enable the input.
 *
 * Every `onEnvironmentSubmit` caller must record + surface failures, because
 * `TaskComposer` propagates the rejection instead of swallowing it.
 * Sharing this hook keeps that failure handling consistent across the
 * redesigned agents page and the legacy AgentsView.
 */
export function useEnvironmentTaskSubmit({
  optimisticTaskListConfig,
  textAreaRef,
  repositoryCloneUrl,
  taskUrlScope = 'repository',
}: UseEnvironmentTaskSubmitOptions) {
  const navigate = useNavigate()
  const {mutateAsync: createEnvironmentTask} = useCreateEnvironmentTask({
    optimisticTaskListConfig,
  })

  // Environment (AHP) task creation is a separate flow from cloud-task creation,
  // so it needs its own failed-attempt state to drive a visible banner. Without
  // this a rejected submission would only be logged and the user would see nothing.
  const [failedEnvironmentAttempts, setFailedEnvironmentAttempts] = useState<FailedEnvironmentAttempt[]>([])

  const onEnvironmentSubmit = useCallback<EnvironmentSubmit>(
    async ({
      environmentId,
      prompt,
      repositoryNwo,
      baseRef,
      managedSandbox,
      agentMode,
      mcpPermissionMode,
      configureSession,
      failureOwner,
      scopeLogin,
      space,
    }: {
      environmentId: string
      prompt: PreparedPrompt
      repositoryNwo?: string
      baseRef?: string
      managedSandbox?: boolean
      agentMode?: AgentMode
      mcpPermissionMode?: McpPermissionMode
      configureSession?: Parameters<EnvironmentSubmit>[0]['configureSession']
      failureOwner?: Parameters<EnvironmentSubmit>[0]['failureOwner']
      scopeLogin?: string
      space?: {owner: string; slug: string}
    }) => {
      try {
        const {task} = await createEnvironmentTask({
          environmentId,
          prompt,
          ...(repositoryNwo ? {repositoryNwo} : {}),
          ...(repositoryNwo && repositoryCloneUrl ? {repositoryCloneUrl} : {}),
          ...(baseRef ? {baseRef} : {}),
          ...(managedSandbox ? {managedSandbox} : {}),
          ...(agentMode ? {agentMode} : {}),
          ...(mcpPermissionMode ? {mcpPermissionMode} : {}),
          ...(configureSession ? {configureSession} : {}),
          ...(!repositoryNwo && !space && scopeLogin ? {scopeLogin} : {}),
          ...(!repositoryNwo && space ? {space} : {}),
        })
        const useGlobalTaskUrl = shouldUseGlobalTaskUrls({
          taskUrlScope,
          taskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_task_detail'),
          repositoryTaskDetailEnabled: isFeatureEnabled('copilot_mission_control_agents_repository_task_detail'),
        })
        navigate(
          generateTaskPathForRepo({
            taskId: task.id,
            ...(!useGlobalTaskUrl && repositoryNwo ? {repositoryNwo} : {}),
          }),
        )
      } catch (error) {
        // Build the attempt in the catch scope so the setState updater closure
        // below captures only this local — referencing the caught `error` both
        // inside that closure and in `throw error` trips a React Compiler
        // invariant (a variable used as both a local and a context reference).
        if (failureOwner !== 'caller') {
          const attempt: FailedEnvironmentAttempt = {
            problemStatement: prompt.text,
            errorMessage: getErrorMessage(error),
            timestamp: new Date().toISOString(),
          }
          setFailedEnvironmentAttempts(prev => [attempt, ...prev])
        }
        // Re-throw so ChatInput's submit handler exits its "Creating..." state
        // and re-enables the input; the banner surfaces the error visibly.
        throw error
      }
    },
    [createEnvironmentTask, navigate, repositoryCloneUrl, taskUrlScope],
  )

  const handleEnvironmentDismiss = useCallback(
    (index: number) => {
      setFailedEnvironmentAttempts(prev => prev.filter((_, i) => i !== index))
      // Dismissing removes the focused Dismiss button, so move focus back to the
      // task input to keep keyboard users oriented (WCAG 2.4.3).
      textAreaRef?.current?.focus()
    },
    [textAreaRef],
  )

  return {onEnvironmentSubmit, failedEnvironmentAttempts, handleEnvironmentDismiss}
}
